import {
  Component,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  ChangeDetectorRef, Renderer2, inject, OnDestroy
} from '@angular/core';
import { Store } from '@ngrx/store';
import { selectHomeFeedSections } from '../store/home-feed.selectors';
import { HomeFeedCard, HomeFeedSection } from '../store/home-feed.model';
import { FindMoneyService } from '@brightside-web/desktop/feature/find-money';
import { HomeFeedService } from '../home-feed.service';
import { SavingsCardService } from '@brightside-web/desktop/feature/savings-account';
import { Subscription } from 'rxjs';
import { CashAdvanceCardService, SpendingCardService } from '@brightside-web/desktop/feature/spending-account';
import { ServiceNotificationsService } from '@brightside-web/desktop/feature/service-notifications';
import { ServiceInventoryService } from '@brightside-web/desktop/data-access/service-inventory';
import { ProgramCardsService } from '@brightside-web/desktop/feature/programs';
import { SurveyCardsService } from "@brightside-web/desktop/feature/survey-questionnaire";
import { LoansCardService } from '@brightside-web/desktop/feature/loans';
import { CreditScoreCardService } from '@brightside-web/desktop/feature/credit-setup';
import { PayrollCardService } from '@brightside-web/desktop/feature/payroll';
import { MySpendingCardService } from '@brightside-web/desktop/feature/my-spending';
import { ChatActionCardService } from '@brightside-web/desktop/feature/chat-action';
import { PlsCardService } from '@brightside-web/desktop/feature/prize-link-savings';
import { WelcomeVideoCardService } from '../welcome-video-card.service';
import { ExternalLendersCardService } from '@brightside-web/desktop/feature/external-lenders';
import {
  ServiceNotificationsCardsService
} from '../../../../service-notifications/src/lib/service-notifications-cards.service';
import { HardshipCardsService } from '@brightside-web/desktop/feature/hardship';
import { CampaignCardsService } from '../../../../campaigns/src/lib/services/campaign-cards.service';
import { CarouselItemComponentLegacy, CarouselItemConfig } from '@brightside/brightside-ui/carousel';

@Component({
    selector: 'brightside-web-home-feed',
    templateUrl: './home-feed.component.html',
    styleUrls: ['./home-feed.component.scss'],
    standalone: false
})
export class HomeFeedComponent implements OnDestroy {
  @ViewChild('urgentNotificationsContainer', { read: ViewContainerRef, static: true }) urgentNotificationsContainer!: ViewContainerRef;
  @ViewChild('promotionContainer', { read: ViewContainerRef, static: true }) promotionContainer!: ViewContainerRef;
  @ViewChild('actionsContainer', { read: ViewContainerRef, static: true }) actionsContainer!: ViewContainerRef;
  @ViewChild('programsContainer', { read: ViewContainerRef, static: true }) programsContainer!: ViewContainerRef;
  @ViewChild('findMoneyContainer', { read: ViewContainerRef, static: true }) findMoneyContainer!: ViewContainerRef;
  @ViewChild('myMoneyInfoContainer', { read: ViewContainerRef, static: true }) myMoneyInfoContainer!: ViewContainerRef;
  @ViewChild('myMoneyCompleteContainer', { read: ViewContainerRef, static: true }) myMoneyCompleteContainer!: ViewContainerRef;
  @ViewChild('myMoneyInitialContainer', { read: ViewContainerRef, static: true }) myMoneyInitialContainer!: ViewContainerRef;
  @ViewChild('myMoneyHelpContainer', { read: ViewContainerRef, static: true }) myMoneyHelpContainer!: ViewContainerRef;
  @ViewChild('repaymentsContainer', { read: ViewContainerRef, static: true }) repaymentsContainer!: ViewContainerRef;
  @ViewChild('payDayContainer', { read: ViewContainerRef, static: true }) payDayContainer!: ViewContainerRef;

  readonly _homeFeedSection = HomeFeedSection;

  sectionVisibility: { [section in HomeFeedSection]: boolean } = {
    [HomeFeedSection.URGENT_NOTIFICATIONS]: false,
    [HomeFeedSection.PROMOTION]: false,
    [HomeFeedSection.ACTIONS]: false,
    [HomeFeedSection.PROGRAMS]: false,
    [HomeFeedSection.FINDMONEY]: false,
    [HomeFeedSection.MYMONEYINFO]: false,
    [HomeFeedSection.MYMONEYCOMPLETE]: false,
    [HomeFeedSection.MYMONEYINITIAL]: false,
    [HomeFeedSection.MYMONEYHELP]: false,
    [HomeFeedSection.REPAYMENTS]: false,
    [HomeFeedSection.PAYDAY]: false
  };

  sectionCount: { [section in HomeFeedSection]: number } = {
    [HomeFeedSection.URGENT_NOTIFICATIONS]: 0,
    [HomeFeedSection.PROMOTION]: 0,
    [HomeFeedSection.ACTIONS]: 0,
    [HomeFeedSection.PROGRAMS]: 0,
    [HomeFeedSection.FINDMONEY]: 0,
    [HomeFeedSection.MYMONEYINFO]: 0,
    [HomeFeedSection.MYMONEYCOMPLETE]: 0,
    [HomeFeedSection.MYMONEYINITIAL]: 0,
    [HomeFeedSection.MYMONEYHELP]: 0,
    [HomeFeedSection.REPAYMENTS]: 0,
    [HomeFeedSection.PAYDAY]: 0
  };

  private storeSubscription: Subscription;
  private renderedCards: Map<string, { componentRef: ComponentRef<any>, section: HomeFeedSection }> = new Map();

  private readonly _store = inject(Store);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _serviceInventory = inject(ServiceInventoryService);
  private readonly _homeFeedService = inject(HomeFeedService);
  private readonly _findMoneyService = inject(FindMoneyService);
  private readonly _savingsCardService = inject(SavingsCardService);
  private readonly _spendingCardService = inject(SpendingCardService);
  private readonly _loansCardService = inject(LoansCardService);
  private readonly _serviceNotificationsService = inject(ServiceNotificationsService);
  private readonly _mpapCardService = inject(ProgramCardsService);
  private readonly _surveyCardsService = inject(SurveyCardsService);
  private readonly _creditScoreCardService = inject(CreditScoreCardService);
  private readonly _payrollCardService = inject(PayrollCardService);
  private readonly _mySpendingCardService = inject(MySpendingCardService);
  private readonly _chatActionCardService = inject(ChatActionCardService);
  private readonly _plsCardService = inject(PlsCardService);
  private readonly _cashAdvanceCardService = inject(CashAdvanceCardService);
  private readonly _externalLendersCardService = inject(ExternalLendersCardService);
  private readonly _welcomeVideoCardService = inject(WelcomeVideoCardService);
  private readonly _serviceNotificationsCardsService = inject(ServiceNotificationsCardsService);
  private readonly _hardshipCardService = inject(HardshipCardsService);
  private readonly _campaignsCardService = inject(CampaignCardsService);

  constructor() {
    this._renderer.addClass(document.body, 'brightside-web-home-feed');
  }

  ngOnInit(): void {
    this._serviceInventory.loadInventory();
  }

  ngAfterViewInit(): void {
    this.storeSubscription = this._store.select(selectHomeFeedSections).subscribe((sections) => {
      setTimeout(() => { this.renderSections(sections) }, 500);
    });
  }

  ngOnDestroy(): void {
    this._renderer.removeClass(document.body, 'brightside-web-home-feed');

    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }

    // Ensure all components are destroyed
    this.renderedCards.forEach(({ componentRef }) => {
      componentRef.destroy();
    });
    this.renderedCards.clear();
  }

  private renderSections(sections: { [section in HomeFeedSection]?: any[] }) {

    const containers = [
      { container: this.urgentNotificationsContainer, section: HomeFeedSection.URGENT_NOTIFICATIONS },
      { container: this.promotionContainer, section: HomeFeedSection.PROMOTION },
      { container: this.actionsContainer, section: HomeFeedSection.ACTIONS },
      { container: this.programsContainer, section: HomeFeedSection.PROGRAMS },
      { container: this.findMoneyContainer, section: HomeFeedSection.FINDMONEY },
      { container: this.myMoneyInfoContainer, section: HomeFeedSection.MYMONEYINFO },
      { container: this.myMoneyCompleteContainer, section: HomeFeedSection.MYMONEYCOMPLETE },
      { container: this.myMoneyInitialContainer, section: HomeFeedSection.MYMONEYINITIAL },
      { container: this.myMoneyHelpContainer, section: HomeFeedSection.MYMONEYHELP },
      { container: this.repaymentsContainer, section: HomeFeedSection.REPAYMENTS },
      { container: this.payDayContainer, section: HomeFeedSection.PAYDAY },
    ];
    containers.forEach(({ container, section }) => {
      const sectionCards = sections[section] || [];

      // REMOVE Cards that are no longer registered
      const existingKeys = Array.from(this.renderedCards.keys()).filter(key => this.renderedCards.get(key)?.section === section);
      const newKeys = sectionCards.map(card => card.key);

      existingKeys.forEach(key => {
        if (!newKeys.includes(key)) {
          const componentRef = this.renderedCards.get(key)?.componentRef;
          if (componentRef) {
            componentRef.destroy();
          }
          this.renderedCards.delete(key);
        }
      });

      sectionCards.forEach((card: HomeFeedCard) => {
        if (!this.renderedCards.has(card.key)) {
          this.injectCard(container, card, section);
        }
      });

      setTimeout(() => {
        this.sectionVisibility[section] = sectionCards.length > 0;
        this.sectionCount[section] = sectionCards.length;
        this._cdRef.detectChanges();
      });
    });

  }

  private injectCard(container: ViewContainerRef, card: HomeFeedCard, section: HomeFeedSection) {
    const componentClass = this._homeFeedService.componentRegistry[card.component];
    if (componentClass) {

      if (section === HomeFeedSection.PROMOTION) {
        const componentRef: ComponentRef<any> = container.createComponent(CarouselItemComponentLegacy);
        componentRef.instance._component.set({data: card, componentRef: componentClass});
        this.renderedCards.set(card.key, { componentRef, section });
      } else {
        const componentRef: ComponentRef<HomeFeedCard> = container.createComponent(componentClass);
        if (card.data) {
          componentRef.instance.data = card.data;
        }
        this.renderedCards.set(card.key, { componentRef, section });
      }

      this._cdRef.detectChanges();
    } else {
      console.warn(`⚠️ Component not found for key: ${card.key}`);
    }
  }

}
